import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';

import type { AccordionItem } from '../../components/Accordion';

export const FAQS: AccordionItem[] = [
  {
    title: 'How does my Opendoor referral link work?',
    description:
      'Share your referral link with potential customers who have a home to sell. ' +
      'When they use your link to request an Opendoor offer on your home, that customer will ' +
      'be attributed to you and appear in your dashboard. You can then use your dashboard to ' +
      'stay up-to-date on your customers’ progress and help support their home trade-in journey.',
  },
  {
    title: 'How does Opendoor work with Realtors?',
    description: (
      <Text as="span" className="text-body-md">
        Opendoor respects the agreements between agents and their customers. If a customer has a
        listing agreement in place, Opendoor will work directly with the agent to facilitate the
        home sale. If there is no listing agreement in place, agents can earn a 1% referral fee if
        they refer the seller to Opendoor before the home is listed{' '}
        <Link
          analyticsName="referral-faq-restrictions-apply"
          href="https://www.opendoor.com/w/agents#faq-referral"
          target="_blank"
          rel="noopener noreferrer"
        >
          (restrictions apply)
        </Link>
        .{' '}
        <Text as="span" className="text-label-md">
          Either way, agents are still able to collect commission from the builder.
        </Text>
      </Text>
    ),
  },
  {
    title: 'How does the cost of selling to Opendoor compare with a traditional sale?',
    description: (
      <Text as="span" className="text-body-md">
        When your customers sells their home to Opendoor, they pay a service charge similar to the
        agent commission in a traditional sale, but they get the convenience of no showings and
        complete control over their moving timeline. You can use this{' '}
        <Link
          analyticsName="referral-faq-comparison-calculator"
          href="https://www.opendoor.com/w/builder-calculator"
          target="_blank"
          rel="noopener noreferrer"
        >
          comparison calculator
        </Link>{' '}
        to help customers to estimate the net proceeds of selling a home to Opendoor vs. the
        traditional way.
      </Text>
    ),
  },
  {
    title: 'What areas does Opendoor buy in?',
    description: (
      <Text as="span" className="text-body-md">
        The fastest way to see if Opendoor can make an offer on a customer's home is to use your
        referral link to launch the Opendoor seller flow and enter the customer's address. Opendoor
        will then tell you whether or not we can make an offer on the home. You can also view the
        markets we operate in{' '}
        <Link
          analyticsName="referral-faq-opendoor-markets"
          href="https://help.opendoor.com/hc/en-us/articles/16913372855053-Where-can-I-sell-my-home-to-Opendoor"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </Link>
        .
      </Text>
    ),
  },
  {
    title: 'What types of homes does Opendoor buy?',
    description: (
      <Text as="span" className="text-body-md">
        Here's a{' '}
        <Link
          analyticsName="referral-faq-home-types-guide"
          href="https://help.opendoor.com/hc/en-us/articles/16914946824077-What-types-of-homes-does-Opendoor-buy"
          target="_blank"
          rel="noopener noreferrer"
        >
          quick guide
        </Link>{' '}
        on the types of homes that Opendoor buys.
      </Text>
    ),
  },
  {
    title: 'Is Opendoor part of my new home contract?',
    description:
      'Opendoor does not represent any parties in your contract or take any of your commission. ' +
      'Our goal is to provide new home sales associates with a frictionless solution to eliminate contingencies, ' +
      'and new homebuyers with a convenient and certain path to sell their home. There is no fee for homebuilders ' +
      'to partner with Opendoor.',
  },
];
